// ===== Work Board — payload contract =====
// board.ts·board-detail.ts·board-register.ts가 함께 쓰는 서버 payload 형태(타입)와, 그 형태를 런타임에
// 확인하는 type guard를 모아둔다. classic script로 로드되므로 board.js보다 먼저 실려야 한다.

type BoardJson = Record<string, unknown>;

interface BoardOwner {
  actorId?: string;
  label?: string;
}

interface BoardWorkspaceScope {
  scope?: string;
}

interface BoardChildProgress {
  ids?: string[];
  total: number;
  done: number;
}

interface BoardCard {
  id: string;
  title?: string;
  status?: string;
  priority?: string;
  owner?: BoardOwner;
  collaborators?: BoardOwner[];
  auditRefs?: unknown[];
  workspaceScope?: BoardWorkspaceScope;
  autoPromoted?: boolean;
  parentId?: string | null;
  children?: BoardChildProgress;
  dueAt?: string | null;
  // 최근 완료 응답(GET /api/work/completed)의 완료 시각.
  completedAt?: string;
  // 서버 decorateBoardLanes 가 싣는 프로젝트 정본 표시(회사 md 이름, 목록에 없으면 source "legacy", 삭제된 프로젝트면 "deleted").
  projectView?: BoardProjectView | null;
}

interface BoardProjectView {
  id?: string;
  title?: string;
  source?: string;
}

interface BoardLane {
  lane: string;
  cards: BoardCard[];
}

interface BoardAuditEvent {
  id?: string;
  action?: string;
  actor?: string;
  target?: string;
  timestamp?: string;
  result?: string;
  writeSet?: unknown[];
  before?: unknown;
  after?: unknown;
}

interface BoardComment {
  body?: string;
  createdAt?: string;
  author?: { actor?: string; displaySnapshot?: { label?: string } };
}

interface BoardLink {
  title?: string;
  uri?: string;
}

interface BoardRegisterCollaborator {
  actorId: string;
  label: string;
}

interface BoardPerson {
  actorId: string;
  label: string;
  isSelf: boolean;
}

interface BoardProjectOption {
  id: string;
  name: string;
  status?: string;
}

// GET /api/work/items/:id 상세 — 편집 base revision·하위 목록·첨부(볼 수 있는 것만)·숨긴 첨부 수.
interface BoardItemRecord {
  id: string;
  title?: string;
  summary?: string;
  status?: string;
  priority?: string;
  owner?: BoardOwner;
  collaborators?: BoardOwner[];
  parentId?: string | null;
  time?: { dueAt?: string | null; plannedStartAt?: string | null } | null;
  project?: { id?: string; title?: string } | null;
  autoPromoted?: boolean;
  // 휴지통에 있는 업무(레인 E2 계약). 단건 읽기만 이 업무를 돌려준다.
  trash?: { at?: string; by?: BoardOwner; rootId?: string } | null;
}

interface BoardChildBrief {
  id: string;
  title?: string;
  status?: string;
  priority?: string;
  owner?: BoardOwner;
  dueAt?: string | null;
}

interface BoardAttachmentView {
  fileId: string;
  attachedAt?: string;
  file?: { id?: string; displayName?: string; mediaType?: string; size?: number; contentUrl?: string };
}

interface BoardItemDetail {
  item: BoardItemRecord;
  revision: string;
  projectView: { id?: string; title?: string; source?: string } | null;
  parent: { id: string; title?: string; missing?: boolean } | null;
  children: BoardChildBrief[];
  childProgress?: BoardChildProgress;
  attachments: BoardAttachmentView[];
  hiddenAttachmentCount: number;
}

interface BoardDom {
  pageStatus: HTMLElement;
  reloadBtn: HTMLButtonElement;
  lifecycleFilter: HTMLSelectElement;
  registerBtn: HTMLButtonElement;
  registerFormRoot: HTMLElement;
  registerBackdrop: HTMLButtonElement;
  laneColumns: HTMLElement;
  summary: HTMLElement;
  boardFilters: HTMLElement;
  detailSidepanelRoot: HTMLElement;
  detailSidepanel: HTMLElement;
  detailBackdrop: HTMLElement;
  detailCloseBtn: HTMLButtonElement;
  detailHeading: HTMLElement;
  detailParentLink: HTMLButtonElement;
  selectionHint: HTMLElement;
  detailContent: HTMLElement;
}

// 팀 보드 카드 필터(백로그·막힘은 레인이 아니라 카드 표시 + 이 필터). 하나만 켠다.
type BoardQuickFilter = "blocked" | "backlog" | "due_soon";

interface BoardState {
  lanes: BoardLane[];
  // 팀 전체·모든 처리 상태의 카드(GET /api/work/board?lifecycle=all&scopes=team) — 내 업무·마감 임박·프로젝트 진척·
  // 상위 제목의 바탕. 팀 보드 필터(범위·처리 상태)와 무관하다. 읽기 전이면 null.
  allCards: BoardCard[] | null;
  // 최근 완료(GET /api/work/completed?days=14&limit=10, 레인 E2 계약) — "처리할 항목"에서도 완료 레인에 보인다. 실패·미지원이면 null.
  recentDone: BoardCard[] | null;
  // 미처리 업무 후보 원본(GET /api/work/outbox?lifecycle=active&scopes=team). 첫 화면 "확인할 것"이 쓴다.
  candidates: unknown[] | null;
  // 프로젝트 레일 선택: null = 전체, "__none__" = 프로젝트 없음, 그 밖 = 프로젝트 id.
  projectFilter: string | null;
  // 상위 카드 아래에서 하위를 펼쳐 둔 상위 업무 id(보는 사람별, localStorage).
  expandedParents: Set<string>;
  selectedItemId: string | null;
  selectedItem: BoardItemRecord | null;
  detail: BoardItemDetail | null;
  auditEvents: BoardAuditEvent[];
  quickFilter: BoardQuickFilter | null;
  // 상태 변경 응답은 받았지만 감사 관측으로 확정하지 못한 업무 — 다시 보내지 못하게 잠근다(custody 불변식).
  ambiguousMutations: Record<string, { auditId?: string; requestedStatus?: string; requestedAt?: string }>;
}

interface BoardStatusOptions { toast?: boolean; autoHide?: boolean }
interface BoardLoadOptions { preserveSelection?: boolean; toastOnSuccess?: boolean }
interface BoardSelectOptions { skipBoardReload?: boolean; focusTitle?: boolean }

// ===== Runtime guards =====

function _boardIsRecord(value: unknown): value is BoardJson {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function _boardIsOwner(value: unknown): value is BoardOwner {
  return _boardIsRecord(value)
    && (value.actorId === undefined || typeof value.actorId === "string")
    && (value.label === undefined || typeof value.label === "string");
}

function _boardIsChildProgress(value: unknown): value is BoardChildProgress {
  return _boardIsRecord(value) && typeof value.total === "number" && typeof value.done === "number"
    && (value.ids === undefined || (Array.isArray(value.ids) && value.ids.every((id) => typeof id === "string")));
}

function _boardIsCard(value: unknown): value is BoardCard {
  return _boardIsRecord(value)
    && typeof value.id === "string"
    && (value.title === undefined || typeof value.title === "string")
    && (value.status === undefined || typeof value.status === "string")
    && (value.priority === undefined || typeof value.priority === "string")
    && (value.owner === undefined || _boardIsOwner(value.owner))
    && (value.collaborators === undefined || (Array.isArray(value.collaborators) && value.collaborators.every(_boardIsOwner)))
    && (value.auditRefs === undefined || Array.isArray(value.auditRefs))
    && (value.workspaceScope === undefined || (_boardIsRecord(value.workspaceScope)
      && (value.workspaceScope.scope === undefined || typeof value.workspaceScope.scope === "string")))
    && (value.autoPromoted === undefined || typeof value.autoPromoted === "boolean")
    && (value.parentId === undefined || value.parentId === null || typeof value.parentId === "string")
    && (value.children === undefined || _boardIsChildProgress(value.children))
    && (value.dueAt === undefined || value.dueAt === null || typeof value.dueAt === "string")
    && (value.projectView === undefined || value.projectView === null || (_boardIsRecord(value.projectView)
      && (value.projectView.id === undefined || typeof value.projectView.id === "string")
      && (value.projectView.title === undefined || typeof value.projectView.title === "string")));
}

function _boardIsLane(value: unknown): value is BoardLane {
  return _boardIsRecord(value)
    && typeof value.lane === "string"
    && Array.isArray(value.cards)
    && value.cards.every(_boardIsCard);
}

function _boardIsAuditEvent(value: unknown): value is BoardAuditEvent {
  if (!_boardIsRecord(value)) return false;
  const stringFields = [value.id, value.action, value.actor, value.target, value.timestamp, value.result];
  return stringFields.every(field => field === undefined || typeof field === "string")
    && (value.writeSet === undefined || Array.isArray(value.writeSet));
}

function _boardIsComment(value: unknown): value is BoardComment {
  return _boardIsRecord(value)
    && (value.body === undefined || typeof value.body === "string")
    && (value.createdAt === undefined || typeof value.createdAt === "string")
    && (value.author === undefined || (_boardIsRecord(value.author)
      && (value.author.actor === undefined || typeof value.author.actor === "string")
      && (value.author.displaySnapshot === undefined || (_boardIsRecord(value.author.displaySnapshot)
        && (value.author.displaySnapshot.label === undefined || typeof value.author.displaySnapshot.label === "string")))));
}

function _boardIsLink(value: unknown): value is BoardLink {
  return _boardIsRecord(value)
    && (value.title === undefined || typeof value.title === "string")
    && (value.uri === undefined || typeof value.uri === "string");
}

function _boardIsItemRecord(value: unknown): value is BoardItemRecord {
  if (!_boardIsRecord(value) || !_boardIsCard(value)) return false;
  const trash: unknown = Reflect.get(value, "trash");
  return trash === undefined || trash === null || _boardIsRecord(trash);
}

function _boardIsChildBrief(value: unknown): value is BoardChildBrief {
  return _boardIsRecord(value) && typeof value.id === "string"
    && (value.title === undefined || typeof value.title === "string")
    && (value.status === undefined || typeof value.status === "string");
}

function _boardIsAttachment(value: unknown): value is BoardAttachmentView {
  return _boardIsRecord(value) && typeof value.fileId === "string"
    && (value.file === undefined || _boardIsRecord(value.file));
}

function _boardParseItemDetail(value: unknown): BoardItemDetail {
  if (!_boardIsRecord(value) || !_boardIsItemRecord(value.item) || typeof value.revision !== "string") {
    throw new Error("Invalid work item detail payload");
  }
  const children = Array.isArray(value.children) ? value.children.filter(_boardIsChildBrief) : [];
  const attachments = Array.isArray(value.attachments) ? value.attachments.filter(_boardIsAttachment) : [];
  const parent = _boardIsRecord(value.parent) && typeof value.parent.id === "string"
    ? { id: value.parent.id, title: typeof value.parent.title === "string" ? value.parent.title : undefined, missing: value.parent.missing === true }
    : null;
  const projectView = _boardIsRecord(value.projectView)
    ? {
      id: typeof value.projectView.id === "string" ? value.projectView.id : undefined,
      title: typeof value.projectView.title === "string" ? value.projectView.title : undefined,
      source: typeof value.projectView.source === "string" ? value.projectView.source : undefined,
    }
    : null;
  return {
    item: value.item,
    revision: value.revision,
    projectView,
    parent,
    children,
    childProgress: _boardIsChildProgress(value.childProgress) ? value.childProgress : undefined,
    attachments,
    hiddenAttachmentCount: typeof value.hiddenAttachmentCount === "number" ? value.hiddenAttachmentCount : 0,
  };
}
